import { dismissToasts } from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { proStudioService, sendProStudioEvent } from '../xstate/app.xstate';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import type { BotType } from '../xstate/prostudio.api-service.types';
import type { ProStudioContext } from '../xstate/prostudio.xstate.types';
import {
  markPublishFailed,
  PUBLISH_IN_PROGRESS_TOAST_ID,
} from './watch-publish-status';

/**
 * Project Bobcat — auto-publish a brand-new solution's first version, so it
 * is testable straight away instead of waiting on an explicit Publish click.
 *
 * Fires exactly ONCE per solution, at the moment its version FIRST becomes
 * publish-ready — see `tableStore.pendingAutoPublish` / `consumePendingAutoPublish`
 * for how that moment is identified, and the `bobcat-auto-publish-on-create`
 * flag for what gates it.
 *
 * Deliberately a STANDALONE implementation, not a refactor of
 * `publish-modal.tsx`'s `onPublish` — that file is shipped, has its own e2e
 * suite, and drives real customer publishes; this only ever needs a SUBSET
 * of its behavior (no modal to close, no environment/survey-bot selection
 * UI), and touching the modal to share code risks the one thing Bobcat rules
 * out: breaking shipped behavior. Both call the same underlying primitives
 * (`sendProStudioEvent('publishBot', …)`, `tableStore`, `watch-publish-status`),
 * so there is nothing to keep in sync beyond those. Like the modal, it leaves
 * COMPLETION to the router-level `usePublishStatusWatcherForCoworkers`
 * (PSTUDIO-3985): that watcher is scoped to the loaded version, lifts the
 * publish lock and announces success, so a second watcher here would only
 * risk running the global restore against another solution.
 *
 * Always targets `env: 'sandbox'` — the same non-production environment the
 * Publish modal itself defaults `selectedEnv` to. `bots` is always `[]`:
 * that parameter only matters for survey-type solutions (`computeSeededBots`
 * in `publish-modal.utils.ts`), and this only ever runs for solutions created
 * by the standard Create Solution wizard, which never produces a survey bot
 * (`create-bot.ts` never sets `botType: 'survey'`) — hence `botType: 'main'`
 * is hardcoded at the call site rather than read from context.
 */
export const AUTO_PUBLISH_ENV = 'sandbox';

/**
 * The solution the route has open: its id once the list has it, else the
 * route's name. Compared before and after the publish answers, so an answer
 * that lands after the person moved on can tell. (project-bobcat keys this on
 * main's `selectOpenSolutionId`, which candidate doesn't have.)
 */
export const openSolutionKey = (
  ctx: Pick<ProStudioContext, 'botName' | 'solutions'>
): string | null =>
  ctx.solutions?.find(
    (s) => s.name === ctx.botName || s.displayName === ctx.botName
  )?.id ??
  ctx.botName ??
  null;

/**
 * A new solution's first publish can be refused with 412 while Bot Manager
 * rebuilds the version after its load-time writes (found live, 2026-09-29).
 * The caller now waits for those writes (`tableStore.flushAutoPublish`), and
 * a refusal gets ONE more try after `RETRY_AFTER_MS` — only while the same
 * solution is still open on the same draft and nothing else is publishing,
 * so it can't land on top of a publish the person started. A second failure
 * says so, and they publish from Deploy.
 */
export const RETRY_AFTER_MS = 10_000;

export const autoPublishSolutionVersion = ({
  draftVersionId,
  botType,
  retry = true,
}: {
  draftVersionId: string;
  botType: BotType;
  /** One more try if this one is refused. */
  retry?: boolean;
}): void => {
  const attemptSolutionId = openSolutionKey(
    proStudioService.getSnapshot().context
  );

  tableStore.setSolutionVersionPublishingInProgress(true);
  tableStore.setState('viewing');
  sendUserMessage({
    type: 'info',
    header: 'Publishing to Sandbox',
    text: "We'll let you know the moment it's ready to test.",
    toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
    autoClose: false,
  });

  sendProStudioEvent({
    type: 'publishBot',
    payload: {
      env: AUTO_PUBLISH_ENV,
      version: draftVersionId,
      botType,
      bots: [],
      onPublishComplete: (result) => {
        const failed = result.status === 'error';
        // The version's status reverts to ACTIVE on a failed publish, and the
        // status watcher reads that as success — mark it failed whatever else
        // happens, so no false "published" toast is ever announced for it.
        if (failed && result.solutionVersionId) {
          markPublishFailed(result.solutionVersionId);
        }

        // The person moved to another solution while this was in flight: the
        // lock, the in-progress toast and the table mode now belong to THAT
        // solution (perhaps to a coworker's publish of it). Touch none of them.
        if (
          openSolutionKey(proStudioService.getSnapshot().context) !==
          attemptSolutionId
        ) {
          return;
        }

        if (failed) {
          tableStore.setSolutionVersionPublishingInProgress(false);
          tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');
          if (retry) {
            setTimeout(() => {
              const ctx = proStudioService.getSnapshot().context;
              if (
                openSolutionKey(ctx) === attemptSolutionId &&
                ctx.draftVersion === draftVersionId &&
                !tableStore.solutionVersionPublishingInProgress
              ) {
                autoPublishSolutionVersion({
                  draftVersionId,
                  botType,
                  retry: false,
                });
              } else {
                dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
              }
            }, RETRY_AFTER_MS);
            return;
          }
          dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
          sendUserMessage({
            type: 'error',
            text: "We couldn't publish this solution automatically. Publish it from Deploy when you're ready.",
            autoClose: 5000,
          });
          return;
        }

        // Completion (lock release, editing back, success toast) is announced
        // by `usePublishStatusWatcherForCoworkers` for the loaded version. With
        // no version for it to follow, release what this raised here.
        if (!result.solutionVersionId) {
          tableStore.setSolutionVersionPublishingInProgress(false);
          dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
          tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');
        }
      },
    },
  });
};
